import * as XLSX from "xlsx";
import type {
  IBulkImportFieldError,
  IBulkImportPreviewResponse,
  IBulkImportPreviewRow,
  IBulkImportRawRow,
  IBulkImportRow,
} from "@/interface/bulkImport.interface";

const OMITTED_ROW_KEYS = new Set(["id", "_id", "key"]);

const OMITTED_META_KEYS = new Set([
  "rowIndex",
  "rowNumber",
  "sNo",
  "index",
  "rowIdx",
  "isValid",
  "status",
  "errors",
  "validationErrors",
  "fieldErrors",
  "messages",
  "__forceValid",
  "data",
  "fields",
  "row",
  "values",
]);

export const normalizeFieldKey = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

export const getFieldValue = (
  data: IBulkImportRow,
  header: string,
): unknown => {
  if (!data) return "";

  if (
    data[header] !== undefined &&
    data[header] !== null &&
    data[header] !== ""
  ) {
    return data[header];
  }

  const headerLower = header.toLowerCase().trim();
  for (const [key, val] of Object.entries(data)) {
    if (key.toLowerCase().trim() === headerLower) {
      return val !== undefined && val !== null ? val : "";
    }
  }

  const headerNorm = normalizeFieldKey(header);
  for (const [key, val] of Object.entries(data)) {
    if (normalizeFieldKey(key) === headerNorm) {
      return val !== undefined && val !== null ? val : "";
    }
  }

  return "";
};

export const getDisplayHeaders = (
  headers: string[],
  rowData: IBulkImportRow,
): string[] => {
  if (headers.length > 0) return headers;

  return Object.keys(rowData || {}).filter((key) => !OMITTED_ROW_KEYS.has(key));
};

const inferFieldError = (
  errorText: string,
  rowData: IBulkImportRow,
): IBulkImportFieldError => {
  const parts = errorText.split(":");
  if (parts.length > 1 && parts[0].trim().length < 30) {
    return {
      field: parts[0].trim(),
      message: parts.slice(1).join(":").trim(),
    };
  }

  const knownKeys = Object.keys(rowData || {});
  for (const key of knownKeys) {
    const humanized = key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase())
      .trim();
    const keyLower = key.toLowerCase();
    const humanLower = humanized.toLowerCase();
    const errLower = errorText.toLowerCase();

    if (
      errLower.startsWith(keyLower) ||
      errLower.startsWith(humanLower) ||
      (keyLower === "email" && errLower.includes("email")) ||
      (keyLower === "employeecode" &&
        (errLower.includes("employee code") ||
          errLower.includes("employeecode"))) ||
      (keyLower === "phone" && errLower.includes("phone")) ||
      (keyLower === "aadhar" && errLower.includes("aadhar")) ||
      (keyLower === "pan" && errLower.includes("pan"))
    ) {
      return { field: key, message: errorText };
    }
  }

  return { field: "General", message: errorText };
};

const normalizeRowErrors = (
  rawErrors: unknown,
  rowData: IBulkImportRow,
): IBulkImportFieldError[] => {
  if (Array.isArray(rawErrors)) {
    return rawErrors.map((errorItem) => {
      if (typeof errorItem === "string") {
        return inferFieldError(errorItem, rowData);
      }

      if (errorItem && typeof errorItem === "object") {
        const errorRecord = errorItem as Record<string, unknown>;
        return {
          field: String(
            errorRecord.field ??
              errorRecord.column ??
              errorRecord.key ??
              errorRecord.name ??
              "General",
          ),
          message: String(
            errorRecord.message ??
              errorRecord.error ??
              errorRecord.msg ??
              "Invalid value",
          ),
        };
      }

      return { field: "General", message: String(errorItem) };
    });
  }

  if (rawErrors && typeof rawErrors === "object") {
    return Object.entries(rawErrors as Record<string, unknown>).map(
      ([field, message]) => ({
        field,
        message:
          typeof message === "string" ? message : JSON.stringify(message),
      }),
    );
  }

  return [];
};

const extractRawRows = (source: Record<string, unknown>): IBulkImportRawRow[] => {
  if (Array.isArray(source.rows)) return source.rows as IBulkImportRawRow[];
  if (Array.isArray(source.preview)) return source.preview as IBulkImportRawRow[];
  if (Array.isArray(source.list)) return source.list as IBulkImportRawRow[];
  if (Array.isArray(source.data)) return source.data as IBulkImportRawRow[];
  if (Array.isArray(source.employees) || Array.isArray(source.users)) {
    return (source.employees || source.users) as IBulkImportRawRow[];
  }
  if (Array.isArray(source.validRows) || Array.isArray(source.invalidRows)) {
    const validRows = ((source.validRows as IBulkImportRawRow[]) || []).map(
      (row) => ({
        ...row,
        __forceValid: true,
      }),
    );
    const invalidRows = ((source.invalidRows as IBulkImportRawRow[]) || []).map(
      (row) => ({
        ...row,
        __forceValid: false,
      }),
    );
    return [...validRows, ...invalidRows];
  }
  if (Array.isArray(source.validData) || Array.isArray(source.invalidData)) {
    const validRows = ((source.validData as IBulkImportRawRow[]) || []).map(
      (row) => ({
        ...row,
        __forceValid: true,
      }),
    );
    const invalidRows = ((source.invalidData as IBulkImportRawRow[]) || []).map(
      (row) => ({
        ...row,
        __forceValid: false,
      }),
    );
    return [...validRows, ...invalidRows];
  }
  if (Array.isArray(source.valid) || Array.isArray(source.invalid)) {
    const validRows = ((source.valid as IBulkImportRawRow[]) || []).map(
      (row) => ({
        ...row,
        __forceValid: true,
      }),
    );
    const invalidRows = ((source.invalid as IBulkImportRawRow[]) || []).map(
      (row) => ({
        ...row,
        __forceValid: false,
      }),
    );
    return [...validRows, ...invalidRows];
  }

  return [];
};

const extractRowData = (item: IBulkImportRawRow): IBulkImportRow => {
  if (
    item.data &&
    typeof item.data === "object" &&
    !Array.isArray(item.data) &&
    Object.keys(item.data).length > 0
  ) {
    return { ...item.data };
  }
  if (
    item.fields &&
    typeof item.fields === "object" &&
    !Array.isArray(item.fields) &&
    Object.keys(item.fields).length > 0
  ) {
    return { ...item.fields };
  }
  if (
    item.row &&
    typeof item.row === "object" &&
    !Array.isArray(item.row) &&
    Object.keys(item.row).length > 0
  ) {
    return { ...item.row };
  }
  if (
    item.values &&
    typeof item.values === "object" &&
    !Array.isArray(item.values) &&
    Object.keys(item.values).length > 0
  ) {
    return { ...item.values };
  }

  const rowData: IBulkImportRow = {};
  Object.entries(item || {}).forEach(([key, value]) => {
    if (!OMITTED_META_KEYS.has(key)) {
      rowData[key] = value as string | number | boolean | null | undefined;
    }
  });

  return rowData;
};

const mergeFlatRowFields = (
  item: IBulkImportRawRow,
  rowData: IBulkImportRow,
): IBulkImportRow => {
  const merged = { ...rowData };

  Object.entries(item).forEach(([key, value]) => {
    if (
      !OMITTED_META_KEYS.has(key) &&
      merged[key] === undefined &&
      typeof value !== "object" &&
      typeof value !== "function"
    ) {
      merged[key] = value as string | number | boolean | null | undefined;
    }
  });

  return merged;
};

export const normalizePreviewResponse = (
  response?: IBulkImportPreviewResponse | IBulkImportRawRow[] | Record<string, unknown> | null,
): {
  headers: string[];
  rows: IBulkImportPreviewRow[];
} => {
  const respObj =
    response && typeof response === "object" && !Array.isArray(response)
      ? (response as Record<string, unknown>)
      : null;
  const payload =
    respObj && respObj.data !== undefined
      ? (respObj.data as Record<string, unknown> | IBulkImportRawRow[])
      : response;

  let headers: string[] = [];

  if (
    payload &&
    typeof payload === "object" &&
    !Array.isArray(payload) &&
    Array.isArray((payload as Record<string, unknown>).headers) &&
    ((payload as Record<string, unknown>).headers as string[]).length > 0
  ) {
    headers = (payload as Record<string, unknown>).headers as string[];
  } else if (
    respObj &&
    Array.isArray(respObj.headers) &&
    (respObj.headers as string[]).length > 0
  ) {
    headers = respObj.headers as string[];
  }

  let rawRows: IBulkImportRawRow[] = [];
  if (Array.isArray(payload)) {
    rawRows = payload as IBulkImportRawRow[];
  } else if (payload && typeof payload === "object") {
    rawRows = extractRawRows(payload as Record<string, unknown>);
  }

  if (
    rawRows.length === 0 &&
    respObj
  ) {
    rawRows = extractRawRows(respObj);
  }

  const rows = rawRows?.map((rawItem, index) => {
    const item = (rawItem || {}) as IBulkImportRawRow;
    const rowIndex =
      item.rowIndex ??
      item.rowNumber ??
      item.sNo ??
      item.index ??
      item.rowIdx ??
      index + 1;

    const baseRowData = extractRowData(item);
    const rowData = mergeFlatRowFields(item, baseRowData);
    const errors = normalizeRowErrors(
      item.errors ?? item.validationErrors ?? item.fieldErrors ?? item.messages,
      rowData,
    );

    let isValid = true;
    if (item.__forceValid !== undefined) {
      isValid = Boolean(item.__forceValid);
    } else if (item.isValid !== undefined) {
      isValid = Boolean(item.isValid);
    } else if (item.status !== undefined) {
      const status = String(item.status).toLowerCase();
      isValid = status === "valid" || status === "true" || status === "success";
    } else {
      isValid = errors.length === 0;
    }

    return {
      rowIndex: Number(rowIndex) || index + 1,
      data: rowData,
      isValid,
      errors,
    };
  });

  if (headers.length === 0) {
    const keySet = new Set<string>();
    rows.forEach((row) => {
      Object.keys(row.data || {}).forEach((key) => {
        if (!OMITTED_ROW_KEYS.has(key)) {
          keySet.add(key);
        }
      });
    });
    headers = Array.from(keySet);
  }

  return { headers, rows };
};

export const buildImportFile = (
  rows: IBulkImportPreviewRow[],
  originalFileName?: string | null,
): File => {
  const rowsData = rows?.map((row) => row?.data);
  const worksheet = XLSX.utils.json_to_sheet(rowsData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Employees");

  const excelBuffer = XLSX.write(workbook, {
    bookType: "xlsx",
    type: "array",
  });

  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  return new File([blob], originalFileName || "bulk_employees_import.xlsx", {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};
